import {
  useAddLocationsToShippingLocationGroupMutation,
  useRemoveLocationsFromShippingLocationGroupMutation,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Select from '@/gf/components/next/Select'
import Spinner from '@/gf/components/Spinner'
import { TableV2, Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import useMsgs from '@/gf/hooks/useMsgs'
import { ApolloError } from '@apollo/client'
import { useEffect, useState } from 'react'
import type { LocationGroup, OrgLocation } from './index'

// Locations sub-tab: set which shipping Locations belong to each Location Group. Mirrors the
// Subscribers multi-select on the Users tab — one optimistic multi-select per group, with the
// add/remove diff routed to the matching mutation so a purchaser subscribed to the group is
// reached for Requests at these locations.
const LocationGroupLocationsTable = ({
  locationGroups,
  locations,
  refetch,
}: {
  locationGroups: LocationGroup[]
  locations: OrgLocation[]
  refetch: () => void
}) => {
  const client = useGqlClient()
  const [_, msgr] = useMsgs()
  const [addLocations] = useAddLocationsToShippingLocationGroupMutation({ client })
  const [removeLocations] = useRemoveLocationsFromShippingLocationGroupMutation({ client })

  const [loadingGroups, setLoadingGroups] = useState<Set<string>>(new Set())
  const [optimisticLocations, setOptimisticLocations] = useState<Map<string, string[]>>(new Map())

  // Keep optimistic state in sync with the latest server data.
  useEffect(() => {
    const next = new Map<string, string[]>()
    locationGroups.forEach((group) => {
      next.set(group.id, group.shippingLocations?.map((l) => l.id) ?? [])
    })
    setOptimisticLocations(next)
  }, [locationGroups])

  // Only active locations are selectable; a current member is always active (the server filters
  // deleted locations out of a group's membership).
  const locationOptions = locations
    .filter((l) => !l.deleted)
    .map((l) => ({ value: l.id, label: l.name }))

  const updateLocations = async (
    locationGroupId: string,
    newLocationIds: string[],
    currentLocationIds: string[]
  ) => {
    setLoadingGroups((prev) => new Set(prev).add(locationGroupId))

    const added = newLocationIds.filter((id) => !currentLocationIds.includes(id))
    const removed = currentLocationIds.filter((id) => !newLocationIds.includes(id))

    // errorPolicy 'all' resolves with { errors } instead of rejecting — the shared error link
    // observes-but-doesn't-rethrow, so a plain .catch never sees GraphQL errors. We inspect the
    // results explicitly so a server rejection (e.g. cross-org location) surfaces a toast.
    const ops: Promise<{ errors?: readonly { message: string }[] }>[] = []
    if (added.length > 0) {
      ops.push(
        addLocations({
          variables: { locationGroupId, shippingLocationIds: added },
          errorPolicy: 'all',
        })
      )
    }
    if (removed.length > 0) {
      ops.push(
        removeLocations({
          variables: { locationGroupId, shippingLocationIds: removed },
          errorPolicy: 'all',
        })
      )
    }

    try {
      const results = await Promise.all(ops)
      const errors = results.flatMap((r) => r.errors ?? [])
      if (errors.length > 0) {
        msgr.add(
          errors.map((e) => e.message).join('; ') || 'Failed to update locations',
          'negative'
        )
      }
    } catch (err) {
      const apolloErr = err as ApolloError
      const message =
        apolloErr.graphQLErrors?.map((error) => error.message).join('; ') || apolloErr.message
      msgr.add(message || 'Failed to update locations', 'negative')
    } finally {
      // Always reconcile with the server (also snaps optimistic state back on failure).
      await refetch()
      setLoadingGroups((prev) => {
        const next = new Set(prev)
        next.delete(locationGroupId)
        return next
      })
    }
  }

  const sortedGroups = [...locationGroups].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="overflow-y-auto max-h-[calc(100vh-20rem)] relative">
      <TableV2 className="min-w-full">
        <Thead className="static after:hidden">
          <Tr className="bg-white">
            <Th>Location Group</Th>
            <Th>Locations</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedGroups.map((group) => {
            const currentIds = optimisticLocations.get(group.id) ?? []
            const isLoading = loadingGroups.has(group.id)

            return (
              <Tr key={group.id}>
                <Td className="font-medium text-gray-900 align-top w-1/3">{group.name}</Td>
                <Td>
                  <div className="relative font-normal min-w-[250px]">
                    <Select
                      isMulti
                      isDisabled={isLoading}
                      options={locationOptions}
                      value={currentIds
                        .map((id) => {
                          const loc = locations.find((l) => l.id === id)
                          return loc ? { value: loc.id, label: loc.name } : null
                        })
                        .filter((opt): opt is { value: string; label: string } => opt !== null)}
                      onChange={(newValue: readonly { value: string; label: string }[]) => {
                        const newIds = newValue.map((opt) => opt.value)
                        setOptimisticLocations((prev) => {
                          const next = new Map(prev)
                          next.set(group.id, newIds)
                          return next
                        })
                        updateLocations(group.id, newIds, currentIds)
                      }}
                      placeholder="Add locations..."
                      isClearable
                    />
                    {isLoading && (
                      <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none">
                        <Spinner size="xs" color="blue" className="mx-0" />
                      </div>
                    )}
                  </div>
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </TableV2>
    </div>
  )
}

export default LocationGroupLocationsTable
